import { useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { DateField, Button, FieldShell, Modal } from "@fawkes/ui";

import { formatDate, todayIso } from "../i18n/format";

// Relaties blokkade ("zwarte lijst") — the banner on the Persoonsgegevens tile + place/edit/withdraw
// dialogs. A faithful port of the Jmix PersoonsgegevensTileFragment.renderBlokkadeUi + BlokkadeDialog +
// IntrekkingDialog. Reads GET /api/relaties/{id}/blokkade via useCustom (relative url — the dataProvider
// prepends /api); writes POST/PUT to the ROLE_RELATIE_BLOKKADE_BEHEER-gated endpoints. canManage drives
// the action buttons; the backend re-enforces the authority.

interface RedenOption {
  code: string;
  label: string;
  toelichtingVerplicht: boolean;
}

interface BlokkadeViewT {
  id: number;
  redenCode: string;
  redenLabel: string;
  toelichting: string | null;
  geldigVan: string | null;
  geldigTot: string | null;
  status: string;
  aangemaaktDoor: string | null;
  aangemaaktOp: string | null;
  ingetrokkenDoor: string | null;
  ingetrokkenOp: string | null;
  intrekkingsReden: string | null;
  version: number | null;
}

interface BlokkadeTabData {
  actief: BlokkadeViewT | null;
  canManage: boolean;
  redenen: RedenOption[];
  geschiedenis: BlokkadeViewT[];
}

type SaveNotice = "saved" | "conflict" | "forbidden" | "error" | null;

function noticeFromError(err: unknown): SaveNotice {
  const code = (err as { statusCode?: number }).statusCode;
  return code === 409 ? "conflict" : code === 403 ? "forbidden" : "error";
}

export function BlokkadeBanner({ relatieId }: { relatieId: string }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { data, refetch } = useCustom({ url: `relaties/${relatieId}/blokkade`, method: "get" });
  const tab = data?.data as BlokkadeTabData | undefined;

  const { mutate, isLoading: saving } = useCustomMutation();
  const [notice, setNotice] = useState<SaveNotice>(null);
  // edit dialog: null = closed; { existing: null } = create; { existing } = view/edit
  const [dialog, setDialog] = useState<{ existing: BlokkadeViewT | null } | null>(null);
  const [withdraw, setWithdraw] = useState<BlokkadeViewT | null>(null);

  if (!tab) return null;
  const { actief, canManage, redenen } = tab;

  const run = (
    url: string,
    method: "post" | "put",
    values: Record<string, unknown>,
    onOk?: () => void,
  ) => {
    setNotice(null);
    mutate(
      { url: `relaties/${relatieId}/blokkade${url}`, method, values },
      {
        onSuccess: () => { onOk?.(); setNotice("saved"); refetch(); },
        onError: (err) => setNotice(noticeFromError(err)),
      },
    );
  };

  const submitDialog = (existing: BlokkadeViewT | null, values: Record<string, unknown>) => {
    if (existing == null) {
      run("", "post", values, () => setDialog(null));
    } else {
      run(`/${existing.id}`, "put", { ...values, version: existing.version }, () => setDialog(null));
    }
  };

  const submitWithdraw = (b: BlokkadeViewT, reden: string) => {
    run(`/${b.id}/intrekken`, "post", { intrekkingsReden: reden, version: b.version }, () => setWithdraw(null));
  };

  const subtitle = (b: BlokkadeViewT) => {
    const parts = [t("relatieDetailView.blokkade.geenAdoptie")];
    if (b.geldigVan) parts.push(`${t("relatieDetailView.blokkade.sinds")} ${formatDate(b.geldigVan, locale)}`);
    if (b.geldigTot) parts.push(`${t("relatieDetailView.blokkade.tot")} ${formatDate(b.geldigTot, locale)}`);
    if (b.redenLabel) parts.push(b.redenLabel);
    return parts.join(" · ");
  };

  const nt: string | null =
    notice === "saved" ? t("relatieDetailView.blokkade.saved")
    : notice === "conflict" ? t("relatieDetailView.blokkade.saveConflict")
    : notice === "forbidden" ? t("relatieDetailView.blokkade.saveForbidden")
    : notice === "error" ? t("relatieDetailView.blokkade.saveError")
    : null;

  return (
    <>
      {actief ? (
        <div className="fawkes-blokkade-banner" role="alert">
          <div className="fawkes-blokkade-banner__header">
            <span className="fawkes-blokkade-banner__icon" aria-hidden="true">⊘</span>
            <span className="fawkes-blokkade-banner__title">{t("relatieDetailView.blokkade.title")}</span>
          </div>
          <span className="fawkes-blokkade-banner__subtitle">{subtitle(actief)}</span>
          <div className="fawkes-blokkade-banner__actions">
            <button type="button" className="fawkes-blokkade-banner__action" onClick={() => { setNotice(null); setDialog({ existing: actief }); }}>
              {t("relatieDetailView.blokkade.details")}
            </button>
            {canManage && (
              <button type="button" className="fawkes-blokkade-banner__action" onClick={() => { setNotice(null); setWithdraw(actief); }}>
                {t("relatieDetailView.blokkade.opheffen")}
              </button>
            )}
          </div>
        </div>
      ) : canManage ? (
        <button type="button" className="fawkes-blokkade-plaats" onClick={() => { setNotice(null); setDialog({ existing: null }); }}>
          <span aria-hidden="true">⊘</span> {t("relatieDetailView.blokkade.plaatsen")}
        </button>
      ) : null}

      {nt && <p className={notice === "saved" ? "fawkes-donateur-notice" : "fawkes-error"} role="status">{nt}</p>}

      {dialog && (
        <BlokkadeDialog
          existing={dialog.existing}
          canManage={canManage}
          redenen={redenen}
          saving={saving}
          onClose={() => setDialog(null)}
          onSubmit={(values) => submitDialog(dialog.existing, values)}
        />
      )}

      {withdraw && (
        <IntrekkingDialog
          saving={saving}
          onClose={() => setWithdraw(null)}
          onSubmit={(reden) => submitWithdraw(withdraw, reden)}
        />
      )}
    </>
  );
}

function BlokkadeDialog({
  existing,
  canManage,
  redenen,
  saving,
  onClose,
  onSubmit,
}: {
  existing: BlokkadeViewT | null;
  canManage: boolean;
  redenen: RedenOption[];
  saving: boolean;
  onClose: () => void;
  onSubmit: (values: Record<string, unknown>) => void;
}) {
  const t = useTranslate();
  const isNew = existing == null;
  // A withdrawn block is read-only; an active block is editable only by a manager.
  const readOnly = !isNew && (!canManage || existing!.status === "INGETROKKEN");

  const [redenCode, setRedenCode] = useState(existing?.redenCode ?? redenen[0]?.code ?? "");
  const [toelichting, setToelichting] = useState(existing?.toelichting ?? "");
  const [geldigVan, setGeldigVan] = useState(existing?.geldigVan ?? todayIso());
  const [geldigTot, setGeldigTot] = useState(existing?.geldigTot ?? "");
  const [err, setErr] = useState<string | null>(null);

  const andersSelected = redenen.find((r) => r.code === redenCode)?.toelichtingVerplicht ?? false;

  const submit = () => {
    if (!redenCode) { setErr(t("relatieDetailView.blokkade.err.reden")); return; }
    if (!geldigVan) { setErr(t("relatieDetailView.blokkade.err.van")); return; }
    if (geldigTot && geldigTot < geldigVan) { setErr(t("relatieDetailView.blokkade.err.totVoorVan")); return; }
    if (andersSelected && !toelichting.trim()) { setErr(t("relatieDetailView.blokkade.err.andersToelichting")); return; }
    onSubmit({
      redenCode,
      toelichting: toelichting.trim() || null,
      geldigVan,
      geldigTot: geldigTot || null,
    });
  };

  return (
    <Modal
      open
      title={t(isNew ? "relatieDetailView.blokkade.dialog.nieuwTitle" : "relatieDetailView.blokkade.dialog.detailsTitle")}
      onClose={onClose}
      footer={
        readOnly ? (
          <Button variant="secondary" onClick={onClose}>{t("common.close")}</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
            <Button variant="primary" disabled={saving} onClick={submit}>
              {t(isNew ? "relatieDetailView.blokkade.plaatsenPrimary" : "common.save")}
            </Button>
          </>
        )
      }
    >
      <div className="fawkes-donateur-form">
        <FieldShell label={t("relatieDetailView.blokkade.form.reden")} required>
          <select className="fui-field__input fui-select" value={redenCode} disabled={readOnly} onChange={(e) => setRedenCode(e.target.value)}>
            {redenen.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
        </FieldShell>
        <FieldShell label={t("relatieDetailView.blokkade.form.toelichting")} required={andersSelected}>
          <textarea className="fui-field__input fawkes-donateur-form__area" maxLength={2000} value={toelichting} readOnly={readOnly} onChange={(e) => setToelichting(e.target.value)} />
        </FieldShell>
        <div className="fawkes-donateur-form__row2">
          <FieldShell label={t("relatieDetailView.blokkade.form.geldigVan")} required>
            <DateField className="fui-field__input" value={geldigVan} readOnly={readOnly} onChange={(e) => setGeldigVan(e.target.value)} />
          </FieldShell>
          <FieldShell label={t("relatieDetailView.blokkade.form.geldigTot")}>
            <DateField className="fui-field__input" value={geldigTot} readOnly={readOnly} onChange={(e) => setGeldigTot(e.target.value)} />
            <span className="fawkes-blokkade-help">{t("relatieDetailView.blokkade.form.geldigTotHelp")}</span>
          </FieldShell>
        </div>
        {existing?.status === "INGETROKKEN" && existing.intrekkingsReden && (
          <p className="fawkes-blokkade-withdrawn">
            {t("relatieDetailView.blokkade.ingetrokkenLabel")}: {existing.intrekkingsReden}
          </p>
        )}
        {err && <p className="fawkes-error">{err}</p>}
      </div>
    </Modal>
  );
}

function IntrekkingDialog({
  saving,
  onClose,
  onSubmit,
}: {
  saving: boolean;
  onClose: () => void;
  onSubmit: (reden: string) => void;
}) {
  const t = useTranslate();
  const [reden, setReden] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const submit = () => {
    if (reden.trim().length < 5) { setErr(t("relatieDetailView.blokkade.err.opheffenReden")); return; }
    onSubmit(reden.trim());
  };

  return (
    <Modal
      open
      title={t("relatieDetailView.blokkade.dialog.opheffenTitle")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={saving} onClick={submit}>{t("relatieDetailView.blokkade.opheffen")}</Button>
        </>
      }
    >
      <div className="fawkes-donateur-form">
        <FieldShell label={t("relatieDetailView.blokkade.form.opheffenReden")} required>
          <textarea className="fui-field__input fawkes-donateur-form__area" maxLength={1000} value={reden} onChange={(e) => setReden(e.target.value)} />
        </FieldShell>
        {err && <p className="fawkes-error">{err}</p>}
      </div>
    </Modal>
  );
}
